import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { twMerge } from "tailwind-merge";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import ArrowButton from "../../components/Form/ArrowButton";
import {
  numberOfDays_Taken_To_Auto_Accept_Order_After_Delivery,
  orderStatus,
} from "../../constants/commonConstants";
import { formatYYYY_MM_DD_HH_MM_A } from "../../utils/dateFormating";

dayjs.extend(duration);

const CountdownTimer = ({
  deliveryDate,
  deliveredDate,
  autoAcceptAt,
  status,
}) => {
  const navigate = useNavigate();
  const thread = useSelector((state) => state.thread);

  const [timeRemaining, setTimeRemaining] = useState({
    days: "00",
    hours: "00",
    minutes: "00",
    seconds: "00",
  });

  const isReviewPeriod = status === orderStatus.ORDER_DELIVERED;
  const reviewPeriodEndDate = autoAcceptAt
    || (
      deliveredDate
        ? new Date(
          new Date(deliveredDate).getTime()
          + numberOfDays_Taken_To_Auto_Accept_Order_After_Delivery * 24 * 60 * 60 * 1000
        ).toISOString()
        : null
    );
  const countdownDate = isReviewPeriod ? reviewPeriodEndDate : deliveryDate;
  const isDeliveryOverdue = !isReviewPeriod && dayjs(formatYYYY_MM_DD_HH_MM_A(countdownDate)).isBefore(dayjs());
  const isReviewPeriodExpired = isReviewPeriod && countdownDate && new Date(countdownDate).getTime() < new Date().getTime();

  // Countdown time logic.
  const timeLeft = (targetDate, useAbsoluteValue = false) => {
    if (!targetDate || isNaN(new Date(targetDate))) {
      return {
        days: "00",
        hours: "00",
        minutes: "00",
        seconds: "00",
      };
    }

    const now = new Date().getTime(); // Current time in milliseconds
    const targetTime = new Date(targetDate).getTime(); // Target time in milliseconds
    const diff = targetTime - now;
    const absDiff = useAbsoluteValue ? Math.abs(diff) : Math.max(diff, 0);

    const days = Math.floor(absDiff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((absDiff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((absDiff / (1000 * 60)) % 60);
    const seconds = Math.floor((absDiff / 1000) % 60);    

    return {
      days: days.toString().padStart(2, "0"),
      hours: hours.toString().padStart(2, "0"),
      minutes: minutes.toString().padStart(2, "0"),
      seconds: seconds.toString().padStart(2, "0"),
    };
  };


  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(timeLeft(countdownDate, isDeliveryOverdue));
    }, 1000); // Update every second

    return () => clearInterval(interval); // Clean up on component unmount
  }, [countdownDate, isDeliveryOverdue, status]);

  const renderButton = () => {
    switch (status) {
      case orderStatus.PENDING_PAYMENT:
        return (
          <ArrowButton
            text="Pay Now"
            onClick={() => {navigate(`/secure-payment/${thread?.selectedOrder?._id}`)}}
            width="242px"
            height="40px"
            iconDirection="right"
            iconPosition="after"
          />
        );
      case orderStatus.ORDER_DELIVERED:
        return null;
      case orderStatus.IN_REVISION:
      case orderStatus.COMPLAINT_RAISED:
      case orderStatus.RESOLVED:
      case orderStatus.COMPLETED:
        return null;
      default:
        return null;
    }
  };

  const renderedButton = renderButton();

  return (
    <div className="mt-[22px]">
      {status !== "Resolved" && status !== "Completed" && (
        <>
          <p className={twMerge("ml-[20px] font-roboto font-semibold text-[18px] leading-5 mb-4", isDeliveryOverdue || isReviewPeriodExpired ? "text-red-500" : "text-primary" )}>
            {isReviewPeriod ? "Revision Period Ends In" : isDeliveryOverdue ? "Overdue By" : "Time Left to Deliver"}
          </p>
          <div className="flex items-center ml-[20px]">
            {["Days", "Hours", "Minutes", "Seconds"].map((label, index) => {
              const value =
                label === "Days"
                  ? timeRemaining.days
                  : label === "Hours"
                  ? timeRemaining.hours
                  : label === "Minutes"
                  ? timeRemaining.minutes
                  : timeRemaining.seconds;

              return (
                <div key={label} className="flex items-center">
                  {/* Divider (before each time block, except the first one) */}
                  {index > 0 && (
                    <div className="h-6 w-[2px] bg-[#E6E7EC] mx-[14px] rounded-full"></div>
                  )}

                  {/* Time Block */}
                  <div className="flex flex-col items-center">
                    <span className={`text-level-4 font-medium leading-[12px] ${isDeliveryOverdue || isReviewPeriodExpired ? "text-red-600" : "text-secondary"}`}>
                      {value}
                    </span>
                    <span className={`text-level-5 mt-2 font-medium leading-3 ${isDeliveryOverdue || isReviewPeriodExpired ? "text-red-600" : "text-secondary"}`}>
                      {label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {renderedButton && (
        <div className="mt-[24px] ml-[20px]">{renderedButton}</div>
      )}
      {status !== "Resolved" && status !== "Completed" && (
        <div className="w-full h-[1px] bg-[#E6E7EC] mt-[24px]"></div>
      )}
    </div>
  );
};

export default CountdownTimer;
